/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { IUserSubprofileModel, useUserSubprofilesSelector } from "@nf/common";
import dynamic from "next/dynamic";
import { useEffect, useRef, useState } from "react";

import ProfileAvatar from "../ProfileMenu/ProfileAvatar";
import { useIsLoading } from "../../../../helpers/setIsLoadingContext";
import {
  useSetCurrentSubProfile,
  useSwitchProfileWithRedirection,
} from "../../../../hooks";
import { useBreakpointsViews } from "components";

import styles from "./HeaderUserInfo.module.scss";

const Portal = dynamic(() => import("components/Portal"), {
  ssr: false,
});

const ProfileMenu = dynamic(() => import("../ProfileMenu"), {
  ssr: false,
});

const SubprofileSelect = dynamic(
  () => import("components/Subprofiles/SubprofileSelect"),
  {
    ssr: false,
  }
);

const EnterPinCodeScreen = dynamic(
  () => import("screens/SubprofileScreen/EnterPinCodeScreen"),
  { ssr: false }
);

export const HeaderUserInfo = () => {
  const { Data: subProfilesData } = useUserSubprofilesSelector();
  const [selectedAuthSubprofile, setSelectedAuthSubprofile] = useState<
    IUserSubprofileModel | undefined
  >(undefined);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const { isMobile, isDesktop } = useBreakpointsViews();

  const selectCurrentProfile = useSetCurrentSubProfile();
  const userRedirect = useSwitchProfileWithRedirection();

  const currentSubprofile = subProfilesData?.currentSubprofile;
  const subprofiles = subProfilesData?.subprofiles;
  const pinCodeId = subProfilesData?.auth?.PinCodeId;
  const { setIsLoading } = useIsLoading();

  const closeEnterPinCodeScreen = () => setSelectedAuthSubprofile(undefined);

  const handleSelectProfile = async (subProfile?: IUserSubprofileModel) => {
    setIsLoading(true);
    selectCurrentProfile(subProfile).then(() => {
      closeEnterPinCodeScreen();
      userRedirect(subProfile);
    });
  };

  const menuRef = useRef<HTMLDivElement>(null);

  // Close the menu when clicking anywhere on the page
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const onSelect = (subprofile: IUserSubprofileModel) => {
    if (subprofile.UserId === currentSubprofile?.UserId) return;

    setIsProfileMenuOpen(false);

    if (pinCodeId && currentSubprofile?.Child && !subprofile?.Child) {
      setSelectedAuthSubprofile(subprofile);
    } else {
      handleSelectProfile(subprofile);
    }
  };

  return (
    <>
      {currentSubprofile && (
        <button
          className={styles.avatar}
          onClick={() => setIsProfileMenuOpen(true)}
        >
          <ProfileAvatar profile={currentSubprofile} />
        </button>
      )}

      {isMobile && isProfileMenuOpen && (
        <Portal>
          <SubprofileSelect
            subprofilesState={subProfilesData}
            setModalVisibility={() => setIsProfileMenuOpen(false)}
          />
        </Portal>
      )}

      {isDesktop && isProfileMenuOpen && currentSubprofile && subprofiles && (
        <div ref={menuRef}>
          <ProfileMenu
            currentSubprofile={currentSubprofile}
            subprofiles={subprofiles}
            onSelect={onSelect}
          />
        </div>
      )}

      {selectedAuthSubprofile && pinCodeId && (
        <Portal>
          <EnterPinCodeScreen
            handleSelectProfile={handleSelectProfile}
            pinCodeId={pinCodeId}
            subprofile={selectedAuthSubprofile}
            close={closeEnterPinCodeScreen}
          />
        </Portal>
      )}
    </>
  );
};

export default HeaderUserInfo;
